import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtDate } from "../lib/utils";
import { Code, Lightning, ListMagnifyingGlass as Activity, Broom } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminMyzeLog() {
  const { t, lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [tab, setTab] = useState("events");
  const [cleaning, setCleaning] = useState(false);

  const load = () => {
    api.get("/admin/myze-events").then(({ data }) => setEvents(data));
    api.get("/admin/unmatched-shops").then(({ data }) => setUnmatched(data));
    api.get("/admin/myze-inbound-log").then(({ data }) => setInbound(data));
  };
  useEffect(() => { load(); }, []);

  const cleanOrphans = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/myze-events/clean-orphans");
      if (data.removed === 0) {
        toast.success(`Nessun evento orfano su ${data.checked} controllati`);
      } else {
        toast.success(`Rimossi ${data.removed} eventi duplicati di ordini eliminati (su ${data.checked}) - ora puoi reimportare lo stesso CSV/ordine`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la pulizia");
    } finally {
      setCleaning(false);
    }
  };

  const resultBadge = (r) => {
    const cls = {
      processed: "tf-status-success",
      auth_failed: "tf-status-error",
      json_invalid: "tf-status-error",
      process_error: "tf-status-error",
    }[r] || "tf-status-warning";
    return <span className={`tf-status-pill ${cls}`}>{r}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-myze-log">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="tf-label">DEBUG</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">Myze Webhook Log</h1>
          <p className="text-sm text-[var(--tf-muted)] mt-2">
            Tutti i payload ricevuti da Myze. Utile per analizzare la struttura degli ordini reali.
          </p>
        </div>
        <button onClick={load} className="tf-btn-secondary text-sm" data-testid="reload-log">Ricarica</button>
        <button onClick={cleanOrphans} disabled={cleaning} className="tf-btn-secondary text-sm flex items-center gap-1.5" data-testid="clean-orphan-events-btn">
          <Broom size={16} weight="bold" /> {cleaning ? "Pulizia…" : "Pulisci eventi di ordini eliminati"}
        </button>
      </div>
      <p className="text-xs text-[var(--tf-muted)] -mt-4">
        Se elimini un ordine e poi reimporti lo stesso CSV e viene segnato come duplicato, usa questo pulsante per sbloccare la reimportazione.
      </p>

      {unmatched.length > 0 && (
        <div className="tf-card p-5 border-2 border-[var(--tf-warning)]">
          <div className="tf-label" style={{ color: "#8a6a00" }}>STORE NON ABBINATI · {unmatched.length}</div>
          <div className="font-display font-bold text-lg mt-1">Nuovi store rilevati</div>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            {unmatched.map((u, i) => (
              <div key={i} className="tf-card p-3 bg-[var(--tf-bg)]">
                <div className="font-mono text-xs">shop: {u.shop_id || "—"}</div>
                <div className="font-mono text-xs text-[var(--tf-muted)]">company: {u.company || "—"}</div>
                <div className="text-xs mt-2">{u.count} ordini in attesa · ultimo {fmtDate(u.last_seen, lang)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-[var(--tf-border)]">
        <button
          onClick={() => { setTab("events"); setSelected(null); }}
          className={`px-4 py-2 text-sm font-display font-bold flex items-center gap-2 border-b-2 transition-colors ${tab === "events" ? "border-[var(--tf-primary)] text-[var(--tf-ink)]" : "border-transparent text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"}`}
          data-testid="tab-events"
        >
          <Lightning size={16} weight="bold" /> Eventi processati ({events.length})
        </button>
        <button
          onClick={() => { setTab("inbound"); setSelected(null); }}
          className={`px-4 py-2 text-sm font-display font-bold flex items-center gap-2 border-b-2 transition-colors ${tab === "inbound" ? "border-[var(--tf-primary)] text-[var(--tf-ink)]" : "border-transparent text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"}`}
          data-testid="tab-inbound"
        >
          <Activity size={16} weight="bold" /> Tutte le chiamate ({inbound.length})
        </button>
      </div>

      {tab === "events" && (
      <div className="grid lg:grid-cols-[400px_1fr] gap-6">
        <div className="tf-card overflow-hidden">
          <div className="p-4 border-b border-[var(--tf-border)] font-display font-bold flex items-center gap-2">
            <Lightning size={18} weight="bold" /> Eventi recenti
          </div>
          <div className="max-h-[600px] overflow-y-auto scroll-thin">
            {events.length === 0 && <div className="p-6 text-center text-[var(--tf-muted)] text-sm">Nessun evento ricevuto</div>}
            {events.map((e) => (
              <button
                key={e.id}
                onClick={() => setSelected(e)}
                className={`w-full text-left p-3 border-b border-[var(--tf-border)] hover:bg-[var(--tf-bg)] ${selected?.id === e.id ? "bg-[var(--tf-bg)]" : ""}`}
                data-testid={`evt-${e.id}`}
              >
                <div className="font-mono text-xs">{e.payload?.data?.externalOrderNumber || e.payload?.data?.orderNumber || e.event_id?.slice(0, 16)}</div>
                <div className="text-xs text-[var(--tf-muted)] mt-1">
                  shop: {e.payload?.data?.store?.name || e.payload?.data?.shopId || "—"} · {fmtDate(e.received_at, lang)}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="tf-card overflow-hidden">
          <div className="p-4 border-b border-[var(--tf-border)] font-display font-bold flex items-center gap-2">
            <Code size={18} weight="bold" /> Payload JSON
          </div>
          {selected ? (
            <pre className="p-4 text-xs font-mono bg-[var(--tf-ink)] text-[var(--tf-primary)] overflow-auto scroll-thin max-h-[600px]" data-testid="payload-preview">
              {JSON.stringify(selected.payload || selected, null, 2)}
            </pre>
          ) : (
            <div className="p-12 text-center text-[var(--tf-muted)]">
              Seleziona un evento dalla lista a sinistra per vederne il payload completo
            </div>
          )}
        </div>
      </div>
      )}

      {tab === "inbound" && (
      <div className="grid lg:grid-cols-[460px_1fr] gap-6">
        <div className="tf-card overflow-hidden">
          <div className="p-4 border-b border-[var(--tf-border)] font-display font-bold flex items-center gap-2">
            <Activity size={18} weight="bold" /> Chiamate in ingresso
            <span className="ml-auto text-xs font-normal text-[var(--tf-muted)]">auth fallita = token errato</span>
          </div>
          <div className="max-h-[600px] overflow-y-auto scroll-thin">
            {inbound.length === 0 && (
              <div className="p-6 text-center text-[var(--tf-muted)] text-sm">
                Nessuna chiamata ricevuta. Se Myze dovrebbe inviare ma non lo vedi qui, il webhook non sta raggiungendo il server.
              </div>
            )}
            {inbound.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className={`w-full text-left p-3 border-b border-[var(--tf-border)] hover:bg-[var(--tf-bg)] ${selected?.id === r.id ? "bg-[var(--tf-bg)]" : ""}`}
                data-testid={`inb-${r.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-xs truncate">{r.remote_ip || "—"}</div>
                  {resultBadge(r.result)}
                </div>
                <div className="text-xs text-[var(--tf-muted)] mt-1">
                  {fmtDate(r.received_at, lang)} · {r.body_size || 0} bytes
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="tf-card overflow-hidden">
          <div className="p-4 border-b border-[var(--tf-border)] font-display font-bold flex items-center gap-2">
            <Code size={18} weight="bold" /> Dettagli chiamata
          </div>
          {selected ? (
            <div className="p-4 space-y-3 text-xs max-h-[600px] overflow-auto scroll-thin">
              <div><span className="tf-label">Risultato</span><div className="mt-1">{resultBadge(selected.result)}</div></div>
              {selected.error && <div><span className="tf-label">Errore</span><pre className="mt-1 text-red-700">{selected.error}</pre></div>}
              <div><span className="tf-label">Headers</span>
                <pre className="mt-1 font-mono bg-[var(--tf-bg)] p-2 rounded">{JSON.stringify(selected.headers, null, 2)}</pre>
              </div>
              <div><span className="tf-label">Body ricevuto</span>
                <pre className="mt-1 font-mono bg-[var(--tf-ink)] text-[var(--tf-primary)] p-2 rounded overflow-auto">{selected.body_preview || "(vuoto)"}</pre>
              </div>
              {selected.process_result && (
                <div><span className="tf-label">Risultato processing</span>
                  <pre className="mt-1 font-mono bg-[var(--tf-bg)] p-2 rounded">{JSON.stringify(selected.process_result, null, 2)}</pre>
                </div>
              )}
            </div>
          ) : (
            <div className="p-12 text-center text-[var(--tf-muted)]">
              Seleziona una chiamata dalla lista a sinistra
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
